/**
 * Currency conversion utilities for Phase 2 enhancements
 * Provides exchange rate lookup and currency conversion functionality
 */

// Simple exchange rate data structure
interface ExchangeRates {
  [key: string]: {
    [key: string]: number;
  };
}

// Mock exchange rates for development (in production, this would come from an API)
// Currently set to 1:1 for all currencies
const MOCK_EXCHANGE_RATES: ExchangeRates = {
  USD: {
    EUR: 1.0,
    GBP: 1.0,
    CAD: 1.0,
    AUD: 1.0,
    JPY: 1.0,
  },
  EUR: {
    USD: 1.0,
    GBP: 1.0,
    CAD: 1.0,
    AUD: 1.0,
    JPY: 1.0,
  },
  GBP: {
    USD: 1.0,
    EUR: 1.0,
    CAD: 1.0,
    AUD: 1.0,
    JPY: 1.0,
  },
  CAD: {
    USD: 1.0,
    EUR: 1.0,
    GBP: 1.0,
    AUD: 1.0,
    JPY: 1.0,
  },
  AUD: {
    USD: 1.0,
    EUR: 1.0,
    GBP: 1.0,
    CAD: 1.0,
    JPY: 1.0,
  },
  JPY: {
    USD: 1.0,
    EUR: 1.0,
    GBP: 1.0,
    CAD: 1.0,
    AUD: 1.0,
  },
};

/**
 * Get exchange rate between two currencies
 */
export function getExchangeRate(fromCurrency: string, toCurrency: string): number {
  if (fromCurrency === toCurrency) {
    return 1.0;
  }

  const rate = MOCK_EXCHANGE_RATES[fromCurrency]?.[toCurrency];
  if (rate) {
    return rate;
  }

  // If direct rate not available, try inverse
  const inverseRate = MOCK_EXCHANGE_RATES[toCurrency]?.[fromCurrency];
  if (inverseRate) {
    return 1 / inverseRate;
  }

  // Fallback to 1.0 if no rate available
  console.warn(`Exchange rate not available for ${fromCurrency} -> ${toCurrency}`);
  return 1.0;
}

/**
 * Convert an amount from one currency to another
 */
export function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string
): number {
  const rate = getExchangeRate(fromCurrency, toCurrency);
  return amount * rate;
}

/**
 * Format amount with conversion indication
 */
export function formatCurrencyWithConversion(
  amount: number,
  originalCurrency: string,
  displayCurrency: string,
  showSymbol = true,
  showOriginal = true
): string {
  if (originalCurrency === displayCurrency) {
    // No conversion needed
    return formatCurrency(amount, displayCurrency, showSymbol);
  }

  const convertedAmount = convertCurrency(amount, originalCurrency, displayCurrency);
  const convertedFormatted = formatCurrency(convertedAmount, displayCurrency, showSymbol);

  if (showOriginal) {
    const originalFormatted = formatCurrency(amount, originalCurrency, showSymbol);
    const rate = getExchangeRate(originalCurrency, displayCurrency);
    return `${convertedFormatted} (${originalFormatted} @ ${rate.toFixed(4)})`;
  }

  return convertedFormatted;
}

/**
 * Enhanced currency formatting with tenant context and conversion
 */
export function formatCurrencyWithTenantAndConversion(
  value: number,
  originalCurrency: string,
  tenantBaseCurrency: string,
  options: {
    showSymbol?: boolean;
    showOriginal?: boolean;
    showConversionRate?: boolean;
  } = {}
): string {
  const {
    showSymbol = true,
    showOriginal = false,
    showConversionRate = false
  } = options;

  if (value === undefined || value === null) {
    return "-";
  }

  // If currencies match, no conversion needed
  if (originalCurrency === tenantBaseCurrency) {
    return formatCurrency(value, tenantBaseCurrency, showSymbol);
  }

  // Convert to tenant currency
  const convertedAmount = convertCurrency(value, originalCurrency, tenantBaseCurrency);
  const convertedFormatted = formatCurrency(convertedAmount, tenantBaseCurrency, showSymbol);

  // Add original currency info if requested
  if (showOriginal) {
    const originalFormatted = formatCurrency(value, originalCurrency, showSymbol);
    
    if (showConversionRate) {
      const rate = getExchangeRate(originalCurrency, tenantBaseCurrency);
      return `${convertedFormatted} (${originalFormatted} @ ${rate.toFixed(4)})`;
    } else {
      return `${convertedFormatted} (originally ${originalFormatted})`;
    }
  }

  return convertedFormatted;
}

// Import formatCurrency from utils
import { formatCurrency } from './utils';

/**
 * Get supported currency list
 */
export function getSupportedCurrencies(): string[] {
  return Object.keys(MOCK_EXCHANGE_RATES);
}

/**
 * Check if currency conversion is available
 */
export function isCurrencyConversionAvailable(fromCurrency: string, toCurrency: string): boolean {
  if (fromCurrency === toCurrency) return true;
  
  return !!(
    MOCK_EXCHANGE_RATES[fromCurrency]?.[toCurrency] ||
    MOCK_EXCHANGE_RATES[toCurrency]?.[fromCurrency]
  );
}